import React, { useState } from 'react';
import { Activity, Maximize2, Minimize2, X } from 'lucide-react';
import './DetailPanel.css';
import { useDrawerExpand } from '../MasterDetail/MasterDetailLayout';

export interface MetadataItem {
  label: string;
  value: string | React.ReactNode;
  highlight?: boolean;
}

export interface DetailPanelProps {
  /** Title displayed in the panel header */
  title: string;
  /** Called when the close (X) button is clicked */
  onClose: () => void;
  /** Header buttons placed before the X (e.g. "Editar" in read mode) */
  headerActions?: React.ReactNode;
  /** Bar of status actions right below the header (e.g. Aprobar, Recibir) */
  toolbar?: React.ReactNode;
  /** 'form' for edit/create, 'div' for the read-only ficha */
  as?: 'form' | 'div';
  /**
   * With as='form': called on submit (submit button or Enter in a field),
   * only after the browser has validated the fields (required, min…).
   */
  onSubmit?: () => void;
  /** Panel content: form fields or read-only sections */
  children: React.ReactNode;
  /** Rendered after the content, inside the body (e.g. submit/delete buttons) */
  footer?: React.ReactNode;
  /**
   * Separate boxes stacked below the panel (DetailCard), for blocks that
   * live apart from the common data: history, lines, related records…
   */
  cards?: React.ReactNode;
  /** Optional metadata rows displayed at the bottom */
  metadata?: MetadataItem[];
  /** Title for the metadata section (e.g. "Metadatos", "Métricas_Rendimiento") */
  metadataTitle?: string;
  /** Additional class name for the outer container */
  className?: string;
  /** Optional click handler on the outer container (e.g. stopPropagation) */
  onClick?: (e: React.MouseEvent) => void;
  /**
   * Drawer: segunda columna de MasterDetailLayout `drawer`, a todo el alto de
   * la zona de contenido, con botón para expandirlo a toda esa zona.
   */
  drawer?: boolean;
  /**
   * Botón de expandir sin ser drawer (p. ej. maestros de Finanzas): al
   * expandir, MasterDetailLayout oculta la tabla y el panel ocupa todo su ancho.
   */
  expandable?: boolean;
}

/**
 * Carcasa común del panel lateral de detalle: la misma para la ficha de
 * lectura y para el formulario, para que ambos se vean y se comporten igual.
 */
export const DetailPanel = ({
  title,
  onClose,
  headerActions,
  toolbar,
  as = 'div',
  onSubmit,
  children,
  footer,
  cards,
  metadata,
  metadataTitle,
  className,
  onClick,
  drawer = false,
  expandable = false,
}: DetailPanelProps) => {
  // Lo guarda el layout, para que sobreviva al cambio ficha → formulario.
  // Fuera de un MasterDetailLayout usa uno propio, que se reinicia al desmontarse.
  const layoutExpand = useDrawerExpand();
  const [localExpanded, setLocalExpanded] = useState(false);
  const isExpanded = layoutExpand ? layoutExpand.isExpanded : localExpanded;
  const setIsExpanded = layoutExpand ? layoutExpand.setIsExpanded : setLocalExpanded;
  const canExpand = drawer || expandable;
  // El drawer es el panel o, con cajas aparte, la pila entera (panel + cajas).
  const drawerClass = `${drawer ? 'detail-drawer' : ''} ${canExpand && isExpanded ? 'is-expanded' : ''}`;

  const body = (
    <>
      <div className="sidebar-form-grid">
        {children}
      </div>
      {footer}
    </>
  );

  // En el drawer con cajas, la cabecera va fuera del panel, como hija de la
  // pila: un sticky solo se queda fijo dentro de su padre, y dentro del panel
  // se iba con él al bajar a las cajas.
  const headerInStack = drawer && !!cards;

  const header = (
    <div className="sidebar-form-header">
      <div className="sidebar-form-title-wrapper">
        <div className="sidebar-form-title-dot" />
        <h3 className="sidebar-form-title">{title}</h3>
      </div>
      <div className="sidebar-form-header-actions">
        {headerActions}
        {canExpand && (
          <button
            type="button"
            onClick={() => setIsExpanded(prev => !prev)}
            className="sidebar-form-close-btn sidebar-form-expand-btn"
            title={isExpanded ? 'Restaurar' : 'Expandir'}
          >
            {isExpanded ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
          </button>
        )}
        <button type="button" onClick={onClose} className="sidebar-form-close-btn">
          <X size={16} />
        </button>
      </div>
    </div>
  );

  const panel = (
    <div
      className={`sidebar-form-section ${cards ? '' : drawerClass} ${className || ''}`}
      onClick={onClick}
      data-keep-detail
    >
      {/* Header */}
      {!headerInStack && header}

      {toolbar && <div className="sidebar-form-toolbar">{toolbar}</div>}

      {/* Body */}
      {as === 'form' ? (
        <form
          className="sidebar-form-body"
          onSubmit={(e) => {
            e.preventDefault();
            if (onSubmit) onSubmit();
          }}
        >
          {body}
        </form>
      ) : (
        <div className="sidebar-form-body">{body}</div>
      )}

      {/* Metadata section */}
      {metadata && metadata.length > 0 && (
        <div className="sidebar-form-metadata">
          <div className="sidebar-form-metadata-header">
            <Activity size={12} className="sidebar-form-metadata-icon" />
            <span className="sidebar-form-metadata-title">
              {metadataTitle || 'Metadatos'}
            </span>
          </div>
          <div className="sidebar-form-metadata-grid">
            {metadata.map((item, idx) => (
              <div key={idx} className="sidebar-form-metadata-item">
                <span className="sidebar-form-metadata-label">{item.label}</span>
                <span
                  className={`sidebar-form-metadata-value ${item.highlight ? 'highlight' : ''}`}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  if (!cards) return panel;

  // Con cajas aparte, la columna es una pila: el panel y debajo sus cajas.
  return (
    <div className={`detail-panel-stack ${drawerClass}`} data-keep-detail>
      {headerInStack && header}
      {panel}
      {cards}
    </div>
  );
};
